import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Truck,
  Package,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Navigation,
  Calendar,
  TrendingUp
} from 'lucide-react';
import { useDriver } from '../context/DriverContext';

const API_URL = API_BASE;

const DriverDashboard = () => {
  const navigate = useNavigate();
  const { activeRoute, deliveryHistory, geoLocation } = useDriver();

  // Dynamic state for routes and stats
  const [todaysRoutes, setTodaysRoutes] = useState([]);
  const [stats, setStats] = useState({
    delivered: 0,
    failed: 0,
    pending: 0,
    distance: '0 km'
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch today's routes from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/delivery/driver/dashboard`);
        if (response.ok) {
          const data = await response.json();
          if (data.routes && data.routes.length > 0) {
            setTodaysRoutes(data.routes);
          }
          if (data.stats) {
            setStats(data.stats);
          }
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  return (
    <div className="p-4 space-y-6" data-testid="driver-dashboard">
      {/* Active Route Banner */}
      {activeRoute && (
        <Card className="border-2 border-green-500 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <Badge className="bg-green-500 text-white mb-2">Active Route</Badge>
                <h3 className="font-semibold text-lg">{activeRoute.id}</h3>
                <p className="text-sm text-muted-foreground">
                  {activeRoute.stops?.filter(s => s.status === 'DELIVERED').length || 0} / {activeRoute.stops?.length || 0} stops completed
                </p>
              </div>
              <Button onClick={() => navigate('/driver/route-active')} className="bg-green-500 hover:bg-green-600">
                Continue
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-2">
        <Card className="border-0 shadow-sm bg-gray-50">
          <CardContent className="p-3 text-center">
            <CheckCircle2 className="h-5 w-5 mx-auto text-green-500 mb-1" />
            <p className="text-xl font-bold">{stats.delivered}</p>
            <p className="text-xs text-gray-500">Delivered</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gray-50">
          <CardContent className="p-3 text-center">
            <XCircle className="h-5 w-5 mx-auto text-red-500 mb-1" />
            <p className="text-xl font-bold">{stats.failed}</p>
            <p className="text-xs text-gray-500">Failed</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gray-50">
          <CardContent className="p-3 text-center">
            <Package className="h-5 w-5 mx-auto text-black mb-1" />
            <p className="text-xl font-bold">{stats.pending}</p>
            <p className="text-xs text-gray-500">Pending</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gray-50">
          <CardContent className="p-3 text-center">
            <TrendingUp className="h-5 w-5 mx-auto text-blue-500 mb-1" />
            <p className="text-xl font-bold">{stats.distance}</p>
            <p className="text-xs text-gray-500">Distance</p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Routes */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Today's Routes
          </h2>
          <Badge variant="outline">{todaysRoutes.length} routes</Badge>
        </div>
        
        {todaysRoutes.map((route) => (
          <Card 
            key={route.id}
            className="cursor-pointer hover:shadow-md active:scale-[0.98] transition-all"
            onClick={() => navigate('/driver/route-load', { state: { routeId: route.id } })}
            data-testid={`route-${route.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl text-white ${
                  route.status === 'READY' ? 'bg-green-500' : 'bg-gray-400'
                }`}>
                  <Truck className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{route.id}</h3>
                    <Badge variant={route.status === 'READY' ? 'default' : 'secondary'}>
                      {route.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {route.stops} stops
                    </span>
                    <span className="flex items-center gap-1">
                      <Package className="h-3 w-3" />
                      {route.packages} pkg
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {route.estimatedTime}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{route.departure}</p>
                  <p className="text-xs text-muted-foreground">{route.zone}</p>
                  <ChevronRight className="h-5 w-5 text-muted-foreground mt-1 ml-auto" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* GPS Status */}
      {geoLocation && (
        <Card className="border-gray-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <Navigation className="h-5 w-5 text-green-500" />
              <div className="flex-1">
                <p className="text-sm font-medium">GPS Active</p>
                <p className="text-xs text-muted-foreground">
                  {(geoLocation.lat ?? 0).toFixed(4)}, {(geoLocation.lng ?? 0).toFixed(4)} • ±{geoLocation.accuracy?.toFixed(0)}m
                </p>
              </div>
              <Badge variant="outline" className="text-green-600 border-green-300">
                <span className="h-2 w-2 rounded-full bg-green-500 mr-1 animate-pulse" />
                Live
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DriverDashboard;
